import { ReportStatus } from "@/interfaces/reports"
import { NotificationPayload } from "@/interfaces/state"
import { isProEnabled } from "@/helpers/FeatureFlagHelper"

const sourceTranslator = {
  'xarid.uzex.uz': 'xarid_uzex',
  'etender.uzex.uz': 'etender',
  'tender.mc.uz': 'mc',
  'hayotbirja.uz': 'xt_xarid',
  'beeline.uz': 'beeline',
  'tenderweek.com': 'tenderweek',
}

const sourceValueToName = {
  'mc': 'tender.mc.uz',
  'xarid_uzex': 'xarid.uzex.uz',
  'etender': 'etender.uzex.uz',
  'xt_xarid': 'xt-xarid.uz',
  'hayot_birja': 'hayotbirja.uz',
  'beeline': 'beeline.uz',
  'tenderweek': 'tenderweek.com',
}

const typeRouteTranslator = {
  'auction': 'Аукцион',
  'selection': 'Отбор',
  'tender': 'Тендер',
  'civil_contract': 'Гражданско-правовой договор',
  'competition': 'Конкурс',
  'framework': 'Рамочное соглашение'
}

const regionRouteTranslator = {
  'andijan': 'Андижанская область',
  'bukhara': 'Бухарская область',
  'jizzakh': 'Джизакская область',
  'qashqadaryo': 'Кашкадарьинская область',
  'navoiy': 'Навоийская область',
  'namangan': 'Наманганская область',
  'karakalpakstan': 'Республика Каракалпакстан',
  'samarqand': 'Самаркандская область',
  'surxondaryo': 'Сурхандарьинская область',
  'sirdaryo': 'Сырдарьинская область',
  'tashkent_region': 'Ташкентская область',
  'fergana': 'Ферганская область',
  'khorezm': 'Хорезмская область',
  'tashkent_city': 'город Ташкент',
}

const categoryRouteTranslator = {
  'agriculture': '01',
  'forestry': '02',
  'fishing': '03',

  'coal': '05',
  'oil-gas': '06',
  'ores': '07',
  'mining-products': '08',
  'mining-services': '09',

  'food': '10',
  'drinks': '11',
  'tobacco': '12',
  'textile': '13',
  'clothes': '14',
  'leather': '15',
  'wood': '16',
  'paper': '17',
  'print-copy': '18',
  'petroleum-coke': '19',
  'chemicals': '20',
  'medical': '21',
  'rubber-and-plastic': '22',
  'non-metallic-minerals': '23',
  'metals': '24',
  'metal-products': '25',
  'computer-electronics': '26',
  'electrical-equipment': '27',
  'machinery': '28',
  'vehicles': '29',
  'vehicles-other': '30',
  'furniture': '31',
  'other-products': '32',
  'repair-installation': '33',

  'energy-hvac': '35',

  'water': '36',
  'water-disposal': '37',
  'waste-collection': '38',
  'reclamation': '39',

  'building-construction': '41',
  'civil-engineering': '42',
  'specialized-construction': '43',

  'vehicles-trade-and-repair': '45',
  'wholesale-trade': '46',
  'retail-trade': '47',

  'land-and-pipeline-transport': '49',
  'water-transport': '50',
  'air-and-space-transport': '51',
  'transport-services': '52',
  'postal-services': '53',

  'accommodation': '55',
  'catering': '56',

  'publishing': '58',
  'media-production': '59',
  'broadcasting': '60',
  'telecommunications': '61',
  'software-development': '62',
  'information-technology': '63',

  'financial-services': '64',
  'insurance-and-pensions': '65',
  'financial-and-insurance-support': '66',

  'real-estate-services': '68',

  'legal-and-accounting-services': '69',
  'management-services': '70',
  'architecture-and-engineering': '71',
  'research-and-development': '72',
  'advertising-and-market-research': '73',
  'professional-scientific-and-technical-services': '74',
  'veterinary-services': '75',

  'rental-and-leasing': '77',
  'employment-and-recruitment-services': '78',
  'tourism-services': '79',
  'security-and-investigations': '80',
  'building-maintenance': '81',
  'administrative-and-support-services': '82',

  'public-administration-and-security': '84',

  'education': '85',

  'healthcare': '86',
  'residential-care': '87',
  'social-services': '88',

  'creativity-arts-and-entertainment': '90',
  'libraries-and-museums': '91',
  'gambling-and-lotteries': '92',
  'sports-and-recreation': '93',

  'public-services': '94',
  'repair-services': '95',
  'personal-services': '96',

  'domestic-services': '97',
  'private-services': '98',

  'extraterritorial-services': '99'
}

interface OpenSitePayload {
  user_id: number;
  username?: string;
}

interface TokenPayload {
  telegram_id: string
}

const decodeBase64Url = (encodedString: string): OpenSitePayload | null => {
  try {
    return JSON.parse(atob(encodedString))
  } catch (error) {
    console.error("Failed to decode open_site payload:", error);
    return null;
  }
};

const decodeJwtToken = (token: string): TokenPayload | null => {
  try {
    const base64 = token.split('.')[1];
    return JSON.parse(atob(base64));
  } catch (error) {
    console.error('Invalid JWT:', error);
    return null;
  }
}

const getProLandingUrl = (): string => {
  const isLocalHost = ["localhost", "127.0.0.1"].includes(window.location.hostname)
  if (isLocalHost) {
    return "http://localhost:3000/pro"
  }
  const isTestHost = window.location.hostname === "test.tender.asia"
  const host = isTestHost ? "test.tender.asia" : "tender.asia"
  return `https://${host}/pro`
}

const reportStatusToNotification: Record<ReportStatus, NotificationPayload>  = {
  [ReportStatus.SUCCESS]: {
    keypath: "notifications.report_sending",
    link: { href: "https://t.me/TenderAsiaBot", text: "t.me/TenderAsiaBot" },
  },
  [ReportStatus.TRIAL_EXPIRED]: {
    keypath: isProEnabled
      ? "notifications.report_expired_trial"
      : "notifications.report_trial_limit",
    link: isProEnabled
      ? { href: getProLandingUrl(), text: getProLandingUrl().replace("https://", "") }
      : { href: "https://tender.asia/reports", text: "tender.asia/reports" },
  },
  [ReportStatus.DAILY_LIMIT]: {
    keypath: "notifications.report_daily_limit",
    link: { href: "https://t.me/TenderAsiaSupport", text: "t.me/TenderAsiaSupport" },
  },
  [ReportStatus.ERROR]: {
    keypath: "notifications.report_error",
  },
};

export { sourceTranslator, sourceValueToName, typeRouteTranslator, regionRouteTranslator, categoryRouteTranslator, decodeBase64Url, decodeJwtToken, reportStatusToNotification as reportStatusToMessage, getProLandingUrl }
